import * as i0 from '@angular/core';
import { Injectable, Pipe, NgModule } from '@angular/core';
import { Subject } from 'rxjs';

/**
 * Compares two arrays and checks if they are identical.
 *
 * @param a Left array
 * @param b Right array
 * @returns If arrays are identical or not
 */
function arrayEquals(a, b) {
  return Array.isArray(a) && Array.isArray(b) && a.length === b.length && a.every((o, i) => o === b[i]);
}
/**
 * Gets cookie's value by name.
 *
 * @param name Cookie to get
 * @returns Cookie's value
 */
function getCookie(name) {
  const cookies = document.cookie.split(';').map(o => o.trim().split('='));
  for (const cookie of cookies) {
    if (cookie[0] === name && cookie.length > 1) {
      return cookie[1];
    }
  }
  return null;
}
/**
 * Sets the cookie.
 *
 * @param name Cookie name
 * @param value Cookie value
 * @param options Additional cookie options
 */
function setCookie(name, value, options = {}) {
  let cookie = `${name}=${value};`;
  if (options.path) cookie += `path=${options.path};`;
  if (options.domain) cookie += `domain=${options.domain};`;
  if (options.expires) cookie += `expires=${options.expires};`;
  document.cookie = cookie;
}
/**
 * Removes cookie by name.
 *
 * @param name Cookie to remove
 * @param options Additional cookie options (must be identical to the set options)
 */
function removeCookie(name, options = {}) {
  const cookieOptions = {
    ...options,
    expires: new Date(0).toString()
  };
  setCookie(name, '', cookieOptions);
}
const BlahDefaultConfig = {
  defaultLocale: 'fi-FI',
  availableLocales: ['fi-FI', 'sv-SE', 'en-US'],
  useLocaleCookie: false,
  resourceNames: ['default'],
  resourceTimeout: 10000,
  resourcePath: 'assets/i18n/messages/',
  appVersion: undefined
};
const BlahLocaleCookieName = 'currentLocale';
/**
 * Blah translation service.
 */
let BlahService = /*#__PURE__*/(() => {
  class BlahService {
    constructor() {
      this.initialized = false;
      this.initializing = false;
      this.locales = [];
      this.locale = BlahDefaultConfig.defaultLocale;
      this.useLocaleCookie = BlahDefaultConfig.useLocaleCookie;
      this.resources = {};
      this.localeChangeSubject = new Subject();
    }
    //#region Public read-only properties
    /**
     * Locales available for translations
     */
    get availableLocales() {
      return [...this.locales];
    }
    /**
     * Current locale used for translations
     */
    get currentLocale() {
      return this.locale;
    }
    /**
     * Available resources that can be used for translations
     */
    get availableResources() {
      return Object.keys(this.resources[this.locale] ?? {});
    }
    /**
     * Locale change observable that is fired when Blah locale changes.
     *
     * New locale is passed to observable as a parameter.
     */
    get localeChange$() {
      return this.localeChangeSubject.asObservable();
    }
    //#endregion Public read-only properties
    //#region Public methods
    ngOnDestroy() {
      this.localeChangeSubject.complete();
    }
    /**
     * Initializes the Blah translation service.
     *
     * @param userConfig User provided Blah configuration
     * @returns Initialization promise
     */
    async init(userConfig) {
      if (this.initialized || this.initializing) {
        throw new Error('[BlahService] Initialize called multiple times.');
      }
      this.initializing = true;
      const config = {
        ...BlahDefaultConfig,
        ...userConfig
      };
      // Copy config locales into memory
      this.locales = [...config.availableLocales];
      this.locale = config.defaultLocale;
      this.useLocaleCookie = config.useLocaleCookie;
      // Fetch user set locale from cookie
      if (this.useLocaleCookie) {
        const localeCookie = getCookie(BlahLocaleCookieName);
        if (localeCookie) {
          if (this.locales.includes(localeCookie)) {
            this.locale = localeCookie;
          } else {
            console.warn('[BlahService]', `Cannot set locale '${localeCookie}' from cookie as it's not in available locales dictionary.`);
          }
        }
      }
      for (const resourceName of config.resourceNames) {
        let timeoutId = -1;
        try {
          // Setup timeout controller and fetch the resource
          const abortController = new AbortController();
          timeoutId = setTimeout(() => abortController.abort(), config.resourceTimeout);
          // FYI: We don't need credentials for fetching resource files
          let fetchUrl = `${config.resourcePath}${resourceName}.json`;
          if (config.appVersion) {
            fetchUrl += `?v=${config.appVersion}`;
          }
          const resp = await fetch(fetchUrl, {
            signal: abortController.signal
          });
          // Finally add the fetched resource to resource dictionary
          const resourceData = await resp.json();
          this.applyResource(resourceName, resourceData);
        } catch (err) {
          let errorMessage = 'Unknown error';
          if (err instanceof Error) {
            if (err.name === 'AbortError') {
              errorMessage = 'Request timeout triggered.';
            } else {
              errorMessage = err.message;
            }
          }
          console.error('[BlahService]', `Failed to load resource '${resourceName}': ${errorMessage}`);
        } finally {
          clearTimeout(timeoutId);
        }
      }
      this.initialized = true;
      this.initializing = false;
    }
    /**
     * Translates given key from available resources (aka blah blah).
     *
     * @param key Resource key or Blah translation config
     * @param locale Translation locale override. If left empty user set locale is be used.
     * @returns Translation for given key
     */
    blah(key, locale) {
      // If key is a plain string (and not empty) just translate it
      if (typeof key === 'string' && key) {
        return this.getTranslation(key, locale);
      }
      // Or if key is a translation object (and not null) translate with parameters
      else if (typeof key === 'object' && key && key.key) {
        let message = this.getTranslation(key.key, locale);
        if (key.params && Array.isArray(key.params) && key.params.length > 0) {
          for (const [i, param] of key.params.entries()) {
            // Convert parameter value to string
            let paramValue = '' + param;
            if (paramValue.startsWith('>')) {
              paramValue = this.getTranslation(paramValue.substring(1), locale);
            }
            message = message.replace(new RegExp(`\\{${i}\\}`, 'g'), paramValue);
          }
        }
        return message;
      }
      throw new Error(`[BlahService] Cannot translate key '${key}' as it's not valid.`);
    }
    /**
     * Changes Blah locale used in translations.
     *
     * @param newLocale New locale (e.g. `'en-US'`). Locale must be in available locales dictionary.
     */
    changeLocale(newLocale) {
      if (!this.locales.includes(newLocale)) {
        throw new Error(`[BlahService] Cannot change locale to '${newLocale}' as it's not in available locales dictionary.`);
      }
      const prevLocale = this.locale;
      this.locale = newLocale;
      if (this.useLocaleCookie) {
        setCookie(BlahLocaleCookieName, this.locale, {
          path: '/'
        });
      }
      // Update locale change subject only if the locale actually changes
      if (this.locale !== prevLocale) {
        this.localeChangeSubject.next(this.locale);
      }
    }
    //#endregion Public methods
    //#region Private methods
    applyResource(name, data) {
      for (const resourceLocale in data) {
        if (resourceLocale in this.resources === false) {
          this.resources[resourceLocale] = {};
        }
        this.resources[resourceLocale][name] = data[resourceLocale];
      }
    }
    getTranslation(key, locale) {
      const definedLocale = locale || this.locale;
      try {
        const path = key.split('.');
        let currentResource = this.resources[definedLocale];
        for (const pathNode of path) {
          currentResource = currentResource[pathNode];
        }
        if (typeof currentResource !== 'string') {
          throw new Error('Missing translation.');
        }
        return currentResource;
      } catch (err) {
        console.warn('[BlahService]', `Missing translation for key '${key}' (${definedLocale}).`);
      }
      return key;
    }
    static {
      this.ɵfac = function BlahService_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BlahService)();
      };
    }
    static {
      this.ɵprov = /* @__PURE__ */i0.ɵɵdefineInjectable({
        token: BlahService,
        factory: BlahService.ɵfac,
        providedIn: 'root'
      });
    }
  }
  return BlahService;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let BlahPipe = /*#__PURE__*/(() => {
  class BlahPipe {
    constructor(blahService, cdRef) {
      this.blahService = blahService;
      this.cdRef = cdRef;
      this.value = '';
      this.key = '';
      this.localeChangeSubscription = this.blahService.localeChange$.subscribe(() => {
        this.updateValue();
        this.cdRef.markForCheck();
      });
    }
    ngOnDestroy() {
      this.localeChangeSubscription.unsubscribe();
    }
    transform(key, params, locale) {
      if (!key) {
        return '';
      }
      if (this.key === key && (
      // First check if both are null/undefined, only after that will compare arrays.
      // That's why we don't compare exact types.
      this.params == params || arrayEquals(this.params, params)) && this.locale == locale) {
        return this.value;
      }
      this.key = key;
      this.params = params && Array.isArray(params) ? [...params] : undefined;
      this.locale = locale;
      this.updateValue();
      return this.value;
    }
    updateValue() {
      const key = typeof this.key === 'string' && this.params && Array.isArray(this.params) ? {
        key: this.key,
        params: this.params
      } : this.key;
      this.value = this.blahService.blah(key, this.locale);
    }
    static {
      this.ɵfac = function BlahPipe_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BlahPipe)(i0.ɵɵdirectiveInject(BlahService, 16), i0.ɵɵdirectiveInject(i0.ChangeDetectorRef, 16));
      };
    }
    static {
      this.ɵpipe = /* @__PURE__ */i0.ɵɵdefinePipe({
        name: "blah",
        type: BlahPipe,
        pure: false
      });
    }
  }
  return BlahPipe;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();
let BlahModule = /*#__PURE__*/(() => {
  class BlahModule {
    static {
      this.ɵfac = function BlahModule_Factory(__ngFactoryType__) {
        return new (__ngFactoryType__ || BlahModule)();
      };
    }
    static {
      this.ɵmod = /* @__PURE__ */i0.ɵɵdefineNgModule({
        type: BlahModule
      });
    }
    static {
      this.ɵinj = /* @__PURE__ */i0.ɵɵdefineInjector({});
    }
  }
  return BlahModule;
})();
(() => {
  (typeof ngDevMode === "undefined" || ngDevMode) && void 0;
})();

/**
 * Generated bundle index. Do not edit.
 */

export { BlahModule, BlahPipe, BlahService };
